export type AuthErrorField = 'email' | 'password' | 'otp' | 'username'

// OCE-1210: api-gateway blocks a nickname change while the fan has in-flight
// tasks (the rename regenerates the personal hashtag). Surfaced inline under
// the Nickname field on /profile/edit, same path as UP-200 "username taken".
const CodeNicknameLockedActiveTasks = 'AG-301'

export type AuthErrorResult =
  | { kind: 'field'; field: AuthErrorField; key: string }
  | { kind: 'view'; action: 'showEmailAlreadyInUse'; key: string }
  | { kind: 'toast'; key: string }

interface MapApiErrorOptions {
  otpContext?: boolean
}

const readHttpResponseErrorCode = (error: object): string | null => {
  const payload = (error as { error?: unknown }).error
  if (typeof payload === 'string') {
    const trimmed = payload.trim()
    return trimmed || null
  }
  if (payload && typeof payload === 'object' && 'code' in payload) {
    const code = (payload as { code?: unknown }).code
    if (typeof code === 'string') {
      return code
    }
  }
  return null
}

export const extractApiErrorCode = (error: unknown): string | null => {
  if (!error || typeof error !== 'object') {
    return null
  }

  const fromHttpResponse = readHttpResponseErrorCode(error)
  if (fromHttpResponse) {
    return fromHttpResponse
  }

  const fetchCode = (error as { error?: { code?: unknown } }).error?.code
  if (typeof fetchCode === 'string') {
    return fetchCode
  }

  const axiosCode = (error as { response?: { data?: { code?: unknown } } })
    .response?.data?.code
  if (typeof axiosCode === 'string') {
    return axiosCode
  }

  return null
}

export const extractApiErrorCodeAsync = async (
  error: unknown
): Promise<string | null> => {
  const direct = extractApiErrorCode(error)
  if (direct) return direct

  if (typeof Response !== 'undefined' && error instanceof Response) {
    if (error.ok || error.bodyUsed) {
      return null
    }
    try {
      const contentType = error.headers.get('content-type') || ''
      if (contentType.includes('application/json')) {
        const body = await error.clone().json()
        const code = (body as { code?: unknown })?.code
        if (typeof code === 'string') {
          return code
        }
        const message = (body as { message?: unknown })?.message
        if (typeof message === 'string' && message.trim()) {
          return message.trim()
        }
      }
      const text = await error.clone().text()
      const trimmed = text.trim()
      return trimmed || null
    } catch {
      return null
    }
  }

  return null
}

const FIELD_MAP: Record<string, { field: AuthErrorField; key: string }> = {
  'AU-004': { field: 'email', key: 'invalidEmail' },
  'AU-005': { field: 'password', key: 'invalidPassword' },
  'AU-006': { field: 'otp', key: 'otpInvalid' },
  'AU-008': { field: 'username', key: 'invalidUsername' },
  'AU-100': { field: 'email', key: 'userNotFound' },
  'AU-205': { field: 'password', key: 'invalidCredentials' },
  // Safety net: if user-profiles UP-100 ever leaks to the client (e.g. from a
  // non-SignIn code path), surface it the same way as invalid credentials.
  'UP-100': { field: 'password', key: 'invalidCredentials' },
  'AU-210': { field: 'otp', key: 'otpInvalid' },
  'AU-211': { field: 'username', key: 'usernameTaken' },
  // OCE-656 (follow-up): user-profiles UpdateUser возвращает UP-200 при
  // попытке занять уже существующий никнейм (без violations[]). Параллель к
  // AU-211 — тот же field/key, другой сервис. Используется в
  // useProfileEditForm для inline-ошибки под полем Nickname на /profile/edit.
  'UP-200': { field: 'username', key: 'usernameTaken' },
  [CodeNicknameLockedActiveTasks]: {
    field: 'username',
    key: 'nicknameLockedActiveTasks'
  },
  // OCE-569 (BUG-F): distinct code for expired OTP — client highlights
  // the Resend CTA instead of telling the user "invalid or expired".
  'AU-213': { field: 'otp', key: 'otpExpired' },
  'AU-301': { field: 'email', key: 'emailRateLimit' },
  // Email-validation errors from EmailVerification (signup step / resend).
  // Rendered inline under the Email field, not as a toast behind the modal.
  'AU-215': { field: 'email', key: 'emailVerificationFailed' },
  'AU-216': { field: 'email', key: 'emailDisposable' },
  'AU-217': { field: 'email', key: 'emailNotExist' },
  'AU-218': { field: 'email', key: 'emailVerificationFailed' },
  'AU-219': { field: 'email', key: 'emailVerificationTimeout' },
  // AU-228: email domain banned by admin, pending manual verification.
  'AU-228': { field: 'email', key: 'emailDomainBanned' }
}

// Codes the resend flow surfaces inside ConfirmationModal (the Email field
// is no longer visible there, so the message goes to the modal error slot).
export const EMAIL_VALIDATION_ERROR_CODES: ReadonlySet<string> = new Set([
  'AU-215',
  'AU-216',
  'AU-217',
  'AU-218',
  'AU-219',
  'AU-228'
])

const TOAST_MAP: Record<string, string> = {
  'AU-200': 'userBlocked',
  'AU-203': 'sessionExpired',
  'AU-204': 'sessionRevoked',
  'AU-208': 'googleOauthFailed',
  'AU-209': 'captchaFailed',
  'AU-010': 'entityTypeRequired',
  'AU-212': 'googleSignupTokenInvalid',
  // OCE-570 (BUG-G): attempted Google OAuth login with a sub that doesn't
  // match the one previously linked to this email — signals collision attempt.
  'AU-214': 'googleAccountMismatch',
  'AU-302': 'accountFrozen',
  'AU-303': 'notInAuthProcess',
  // BAT-1092 athlete sign-up guards.
  'AU-013': 'athleteReferralNotAllowed',
  'AU-014': 'termsRequired',
  'AU-229': 'googleEmailNotVerified',
  'AU-304': 'verificationResendTooSoon'
}

// Internal: pure code → AuthErrorResult mapping, shared by sync/async entry
// points. Keeps switch logic single-source.
const codeToResult = (
  code: string | null,
  options: MapApiErrorOptions
): AuthErrorResult => {
  if (!code) {
    return { kind: 'toast', key: 'generic' }
  }

  if (code === 'AU-206') {
    return {
      kind: 'view',
      action: 'showEmailAlreadyInUse',
      key: 'emailAlreadyInUse'
    }
  }

  if (code === 'AU-300') {
    return options.otpContext
      ? { kind: 'field', field: 'otp', key: 'tooManyAttempts' }
      : { kind: 'toast', key: 'tooManyAttempts' }
  }

  const fieldHit = FIELD_MAP[code]
  if (fieldHit) {
    return { kind: 'field', ...fieldHit }
  }

  const toastHit = TOAST_MAP[code]
  if (toastHit) {
    return { kind: 'toast', key: toastHit }
  }

  return { kind: 'toast', key: 'generic' }
}

export const mapApiError = (
  error: unknown,
  options: MapApiErrorOptions = {}
): AuthErrorResult => codeToResult(extractApiErrorCode(error), options)

export const mapApiErrorAsync = async (
  error: unknown,
  options: MapApiErrorOptions = {}
): Promise<AuthErrorResult> =>
  codeToResult(await extractApiErrorCodeAsync(error), options)

// Single source of truth for every backend auth code the client understands.
// Analytics' error_type normaliser (src/lib/analytics/errorType.ts) asserts it
// maps a human-readable label for each of these, so adding a new code here (or
// to FIELD_MAP/TOAST_MAP) fails that test until an analytics label is added too.
export const KNOWN_AUTH_ERROR_CODES: ReadonlySet<string> = new Set<string>([
  ...Object.keys(FIELD_MAP),
  ...Object.keys(TOAST_MAP),
  'AU-206', // email already in use (special-cased in codeToResult)
  'AU-300' // too many attempts (special-cased in codeToResult)
])
